import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";

import { useAuth } from "@/auth/AuthContext";
import { useBranding } from "@/branding/BrandingLoader";
import { ActiveAccountSwitcher } from "@/components/ActiveAccount";
import { ChatWidget } from "@/components/ChatWidget";
import { CommandPalette } from "@/components/CommandPalette";
import { KeyboardHelp } from "@/components/KeyboardHelp";
import { NotificationsCenter } from "@/components/NotificationsCenter";
import { Onboarding } from "@/components/Onboarding";
import { OrgSwitcher } from "@/components/OrgSwitcher";
import { StatusRibbon } from "@/components/StatusRibbon";
import { ThemeToggle } from "@/components/Theme";
import { Badge } from "@/components/ui";
import { useT, setLocale, SUPPORTED_LOCALES, type Locale } from "@/i18n";
import { useWebSocketState } from "@/ws/WebSocketProvider";

const NAV_TRADING: { to: string; key: string; label: string }[] = [
  { to: "/terminal", key: "nav.terminal", label: "Terminal" },
  { to: "/portfolio", key: "nav.portfolio", label: "Portfolio" },
  { to: "/orders", key: "nav.orders", label: "Orders" },
  { to: "/fills", key: "nav.fills", label: "Fills" },
  { to: "/alerts", key: "nav.alerts", label: "Alerts" },
  { to: "/position-history", key: "nav.history", label: "History" },
  { to: "/commissions", key: "nav.commissions", label: "Commissions" },
  { to: "/statements", key: "nav.statements", label: "Statements" },
  { to: "/appropriateness", key: "nav.appropriateness", label: "Appropriateness" },
  { to: "/complaints", key: "nav.complaints", label: "Complaints" },
  { to: "/discover", key: "nav.discover", label: "Discover" },
  { to: "/academy", key: "nav.academy", label: "Academy" },
  { to: "/strategies", key: "nav.strategies", label: "Strategies" },
  { to: "/robots", key: "nav.robots", label: "Robots" },
  { to: "/analytics", key: "nav.analytics", label: "Analytics" },
  { to: "/tearsheet", key: "nav.tearsheet", label: "Tearsheet" },
  { to: "/trading-accounts", key: "nav.accounts", label: "Accounts" },
  { to: "/withdrawals", key: "nav.withdrawals", label: "Withdrawals" },
  { to: "/broker-accounts", key: "nav.broker_legacy", label: "Broker connects (legacy)" },
];

/**
 * Admin navigation, grouped by operator function. Each item carries the
 * permission(s) that grant access (`perm`, matched with hasAny). The menu
 * mirrors the route guards in App.tsx so we never show a link that would
 * 403. A group renders only when the user can see at least one of its items.
 */
type AdminNavItem = { to: string; label: string; perm: string[] };
const NAV_ADMIN_GROUPS: { label: string; items: AdminNavItem[] }[] = [
  {
    label: "Overview",
    items: [
      { to: "/admin/dashboard", label: "Dashboard", perm: ["admin:full"] },
      { to: "/admin/bi", label: "Business intelligence", perm: ["admin:full"] },
      { to: "/admin/tca", label: "Cost analysis (TCA)", perm: ["admin:full"] },
    ],
  },
  {
    label: "Clients & Access",
    items: [
      { to: "/admin/organizations", label: "Tenants", perm: ["admin:full"] },
      { to: "/admin/users", label: "Users", perm: ["users:manage", "admin:full"] },
      { to: "/admin/roles", label: "Roles", perm: ["users:manage", "admin:full"] },
      { to: "/admin/account-groups", label: "Account groups", perm: ["admin:full"] },
    ],
  },
  {
    label: "Compliance",
    items: [
      { to: "/admin/sanctions", label: "Sanctions", perm: ["admin:full"] },
      { to: "/admin/aml", label: "AML alerts", perm: ["admin:full"] },
      { to: "/admin/surveillance", label: "Surveillance", perm: ["admin:full"] },
      { to: "/admin/complaints", label: "Complaints", perm: ["admin:full"] },
      { to: "/admin/best-execution", label: "Best execution", perm: ["admin:full"] },
      { to: "/admin/rts22", label: "Transaction reports", perm: ["admin:full"] },
      { to: "/admin/pending-changes", label: "Cooling-off changes", perm: ["admin:full"] },
      { to: "/admin/tax-documents", label: "Tax documents", perm: ["admin:full"] },
      { to: "/admin/audit", label: "Audit log", perm: ["audit:read", "admin:full"] },
    ],
  },
  {
    label: "Funding",
    items: [
      { to: "/admin/deposits", label: "Deposits", perm: ["admin:full"] },
      { to: "/admin/withdrawals", label: "Withdrawals", perm: ["admin:full"] },
      { to: "/admin/treasury", label: "Treasury", perm: ["admin:full"] },
      { to: "/admin/margin-calls", label: "Margin calls", perm: ["admin:full"] },
    ],
  },
  {
    label: "Trading ops",
    items: [
      {
        to: "/admin/dealing-desk",
        label: "Dealing desk",
        perm: ["dealing_desk:read", "dealing_desk:approve", "admin:full"],
      },
      { to: "/admin/lp-sessions", label: "LP sessions", perm: ["brokers:manage", "admin:full"] },
      { to: "/admin/risk-limits", label: "Risk limits", perm: ["admin:full"] },
    ],
  },
  {
    label: "Commercial",
    items: [
      { to: "/admin/ib", label: "Introducing brokers", perm: ["admin:full"] },
      { to: "/admin/shop", label: "Shop", perm: ["shop:manage", "admin:full"] },
      { to: "/admin/promo-codes", label: "Promo codes", perm: ["admin:full"] },
      { to: "/admin/marketing", label: "Marketing", perm: ["admin:full"] },
    ],
  },
  {
    label: "Accounting",
    items: [
      { to: "/admin/gl", label: "General ledger", perm: ["admin:full"] },
      { to: "/admin/reconciliation", label: "Reconciliation", perm: ["admin:full"] },
      { to: "/admin/corporate-actions", label: "Corporate actions", perm: ["admin:full"] },
      { to: "/admin/funding-rates", label: "Funding rates", perm: ["admin:full"] },
    ],
  },
  {
    label: "Platform",
    items: [
      { to: "/admin/branding", label: "Branding", perm: ["admin:full"] },
      { to: "/admin/custom-domains", label: "Custom domains", perm: ["admin:full"] },
      { to: "/admin/oidc", label: "OIDC / SSO", perm: ["admin:full"] },
      { to: "/admin/kyc", label: "KYC providers", perm: ["admin:full"] },
      { to: "/admin/feature-flags", label: "Feature flags", perm: ["admin:full"] },
      { to: "/admin/webhooks", label: "Webhooks", perm: ["admin:full"] },
      { to: "/admin/copy-trading", label: "Copy trading", perm: ["admin:full"] },
      { to: "/admin/push", label: "Push notifications", perm: ["admin:full"] },
      { to: "/admin/status", label: "System status", perm: ["admin:full"] },
      { to: "/admin/usage", label: "Usage & billing", perm: ["admin:full"] },
    ],
  },
];

export function Layout() {
  const { user, hasAny, logout } = useAuth();
  const branding = useBranding();
  const { state: wsState } = useWebSocketState();
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const orgName = branding?.name ?? "Piggy";

  return (
    <div className="flex h-screen flex-col bg-term-bg">
      <header className="flex h-12 shrink-0 items-center justify-between border-b border-term-border bg-term-pane px-3 sm:px-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setMobileNavOpen((v) => !v)}
            aria-label="Toggle navigation"
            className="rounded-sm p-1 text-term-dim hover:bg-term-elev hover:text-term-fg sm:hidden"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm font-semibold"
          >
            {branding?.branding.logo_url ? (
              <img src={branding.branding.logo_url as string} alt={orgName} className="h-6" />
            ) : (
              <span className="rounded-sm bg-brand px-1.5 py-0.5 text-black">P</span>
            )}
            <span className="hidden tracking-wide sm:inline">{orgName.toUpperCase()}</span>
          </button>
        </div>
        <div className="flex items-center gap-2 text-sm text-term-dim">
          <kbd
            className="hidden rounded-sm border border-term-border bg-term-bg px-1.5 py-0.5 text-2xs text-term-mute md:inline-block"
            title="Press to open command palette"
          >
            ⌘K
          </kbd>
          <WsBadge state={wsState} />
          <ActiveAccountSwitcher />
          <NotificationsCenter />
          <OrgSwitcher />
          <LocaleSwitcher />
          <ThemeToggle />
          {user && (
            <>
              <span className="hidden md:inline" title={user.email}>
                {user.email}
              </span>
              {user.totp_enabled && (
                <span title="MFA enabled" className="up">
                  ●
                </span>
              )}
              <button onClick={logout} className="rounded-sm px-2 py-1 text-xs text-term-dim hover:bg-term-elev hover:text-term-fg">
                Sign out
              </button>
            </>
          )}
        </div>
      </header>
      <div className="flex flex-1 overflow-hidden">
        <nav
          className={`${mobileNavOpen ? "block" : "hidden"} sm:block w-48 shrink-0 overflow-y-auto border-r border-term-border bg-term-pane p-2`}
          onClick={() => setMobileNavOpen(false)}
        >
          <NavSection label="Trading" items={NAV_TRADING} />
          {NAV_ADMIN_GROUPS.map((group) => {
            const visible = group.items.filter((it) => hasAny(...it.perm));
            if (visible.length === 0) return null;
            return <NavSection key={group.label} label={group.label} items={visible} />;
          })}
          <NavSection
            label="Account"
            items={[
              { to: "/profile", label: "Profile & MFA" },
              { to: "/api-keys", label: "API keys" },
            ]}
          />
        </nav>
        <div className="flex flex-1 flex-col">
          <StatusRibbon />
          <main className="flex-1 overflow-y-auto bg-term-bg p-4 sm:p-6">
            <Outlet />
          </main>
          <ComplianceFooter />
        </div>
      </div>

      {/* Global overlays — registered once, listen everywhere. */}
      <CommandPalette />
      <KeyboardHelp />
      <Onboarding />
      <ChatWidget />
    </div>
  );
}

/**
 * Phase 10A.1 / 10A.14 — global compliance footer.
 *
 * Renders the licensed-entity attribution required by every regulator:
 * entity name + license + compensation scheme + risk warning. Stays
 * silent when the tenant hasn't configured a licensed_entity (e.g.
 * the dev default org), so unregulated/internal-only deployments
 * don't show empty disclosure boxes.
 */
function ComplianceFooter() {
  const branding = useBranding();
  const ent = branding?.licensed_entity;
  if (!ent) return null;

  const compensationLabel = (() => {
    switch (ent.compensation_scheme) {
      case "ICF":
        return "Investor Compensation Fund (Cyprus) — up to €20,000";
      case "FSCS":
        return "Financial Services Compensation Scheme — up to £85,000";
      case "SIPC":
        return "Securities Investor Protection Corporation — up to $500,000";
      case "EDB":
        return "Entschädigungseinrichtung deutscher Banken — up to €100,000";
      case "NONE":
        return "No investor compensation scheme applies.";
      default:
        return null;
    }
  })();

  return (
    <footer className="border-t border-term-border bg-term-elev px-4 py-3 text-[11px] leading-relaxed text-term-dim sm:px-6">
      {ent.risk_warning_html && (
        <div
          className="mb-2 text-amber-600 dark:text-amber-400"
          // The HTML is admin-authored and stored on the org row; not
          // user-supplied, so direct injection is acceptable here.
          dangerouslySetInnerHTML={{ __html: ent.risk_warning_html }}
        />
      )}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="font-medium text-term-fg">{ent.name}</span>
        {ent.regulatory_authority && ent.license_number && (
          <span>
            {ent.regulatory_authority} · License {ent.license_number}
          </span>
        )}
        {ent.jurisdiction && <span>· {ent.jurisdiction}</span>}
        {compensationLabel && <span>· {compensationLabel}</span>}
        {ent.terms_url && (
          <a className="underline hover:text-brand" href={ent.terms_url} target="_blank" rel="noreferrer noopener">
            Terms
          </a>
        )}
        {ent.privacy_url && (
          <a className="underline hover:text-brand" href={ent.privacy_url} target="_blank" rel="noreferrer noopener">
            Privacy
          </a>
        )}
        {ent.complaints_email && (
          <a className="underline hover:text-brand" href={`mailto:${ent.complaints_email}`}>
            Complaints
          </a>
        )}
      </div>
    </footer>
  );
}

function LocaleSwitcher() {
  const { locale } = useT();
  return (
    <select
      aria-label="Language"
      value={locale}
      onChange={(e) => setLocale(e.target.value as Locale)}
      className="rounded-sm bg-term-elev px-1 py-0.5 text-xs text-term-dim"
    >
      {SUPPORTED_LOCALES.map((l) => (
        <option key={l} value={l}>
          {l.toUpperCase()}
        </option>
      ))}
    </select>
  );
}


function NavSection({
  label,
  items,
}: {
  label: string;
  items: { to: string; label: string; key?: string }[];
}) {
  const { t } = useT();
  return (
    <div className="mb-4">
      <div className="mb-1 px-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
        {label}
      </div>
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          className={({ isActive }) =>
            `block rounded-md px-2 py-1.5 text-sm ${
              isActive
                ? "bg-brand/10 text-brand"
                : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
            }`
          }
        >
          {it.key ? t(it.key) : it.label}
        </NavLink>
      ))}
    </div>
  );
}

function WsBadge({ state }: { state: "idle" | "connecting" | "open" | "closed" }) {
  const label = { idle: "—", connecting: "…", open: "live", closed: "reconn." }[state];
  const tone = state === "open" ? "good" : state === "connecting" ? "warn" : "neutral";
  return (
    <Badge tone={tone as "good" | "warn" | "neutral"}>
      <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-current" />
      {label}
    </Badge>
  );
}
